import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * API para gestión de Conductores (Directorio)
 * Maneja la comunicación con el backend para obtener y modificar conductores
 */

/**
 * Obtener conductores paginados y filtrados
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - { pageSize, pageNumber, liveSearch, empresa_id }
 * @returns {Promise} Response con lista de conductores y total
 */
export const getConductores = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/conductores/?';
    const query = [];
    if (params.pageSize) query.push(`pageSize=${params.pageSize}`);
    if (params.pageNumber) query.push(`pageNumber=${params.pageNumber}`);
    if (params.searchValue && params.searchValue.length > 0) query.push(`searchValue=${encodeURIComponent(params.searchValue)}`);
    if (params.empresa_id) query.push(`empresa_id=${params.empresa_id}`);
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    if (response.data) {
      return {
        success: true,
        data: response.data.data,
        total: response.data.total,
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al obtener conductores:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener conductores',
      data: [],
      total: 0
    };
  }
};

/**
 * Obtener conductor por ID
 * @param {Object} user - Usuario autenticado
 * @param {number} conductorId - ID del conductor
 * @returns {Promise} Response con datos del conductor
 */
export const getConductorById = async (user, conductorId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi}/mantenedores/conductores/${conductorId}`;
    const response = await axios.get(endpoint, config);
    if (response.data) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: null
    };
  } catch (error) {
    console.error('Error al obtener conductor por ID:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener conductor',
      data: null
    };
  }
};

/**
 * Actualizar conductor
 * @param {Object} user - Usuario autenticado
 * @param {number} conductorId - ID del conductor
 * @param {Object} conductorData - Datos a actualizar
 * @returns {Promise} Response con resultado de la operación
 */
export const updateConductor = async (user, conductorId, conductorData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi}/mantenedores/conductores/${conductorId}`;
    const response = await axios.put(endpoint, conductorData, config);
    return {
      success: true,
      data: response.data,
      message: 'Conductor actualizado exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar conductor:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar conductor',
      details: error.response?.data
    };
  }
};

/**
 * Eliminar conductor
 * @param {Object} user - Usuario autenticado
 * @param {number} conductorId - ID del conductor
 * @returns {Promise} Response con resultado de la operación
 */
export const deleteConductor = async (user, conductorId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi}/mantenedores/conductores/${conductorId}`;
    const response = await axios.delete(endpoint, config);
    return {
      success: true,
      message: 'Conductor eliminado exitosamente'
    };
  } catch (error) {
    console.error('Error al eliminar conductor:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al eliminar conductor',
      details: error.response?.data
    };
  }
};

/**
 * Obtener conteo de conductores con datos faltantes
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con el conteo de errores
 */
export const getConductoresValidationCount = async (user) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi}/mantenedores/conductores/validation/count`;
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    }
    
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: { count: 0 }
    };
  } catch (error) {
    console.error('Error al obtener conteo de validaciones:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener conteo de validaciones',
      data: { count: 0 }
    };
  }
};

/**
 * Obtener lista de conductores con datos faltantes
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - { pageSize, pageNumber, searchValue }
 * @returns {Promise} Response con lista de conductores con errores
 */
export const getConductoresValidationList = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/conductores/validation/list?';
    
    const query = [];
    if (params.pageSize) query.push(`pageSize=${params.pageSize}`);
    if (params.pageNumber) query.push(`pageNumber=${params.pageNumber}`);
    if (params.searchValue && params.searchValue.length > 0) {
      query.push(`searchValue=${encodeURIComponent(params.searchValue)}`);
    }
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data,
        total: response.data.total,
        message: response.data.message
      };
    }
    
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al obtener lista de validaciones:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener lista de validaciones',
      data: [],
      total: 0
    };
  }
};

/**
 * Asignar datos a usuario móvil
 * @param {Object} user - Usuario autenticado
 * @param {number} usuarioMovilId - ID del usuario móvil
 * @param {Object} assignData - Datos a asignar
 * @returns {Promise} Response con resultado de la operación
 */
export const assignUsuarioMovil = async (user, usuarioMovilId, assignData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + `/mantenedores/conductores/usuarios-movil/${usuarioMovilId}/assign`;
    const response = await axios.put(endpoint, assignData, config);
    
    return {
      success: true,
      data: response.data,
      message: 'Usuario móvil actualizado exitosamente'
    };
  } catch (error) {
    console.error('Error al asignar usuario móvil:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al asignar usuario móvil',
      details: error.response?.data
    };
  }
};

/**
 * Buscar usuarios móviles por valor de búsqueda
 * @param {Object} user - Usuario autenticado
 * @param {string} searchValue - Valor de búsqueda
 * @returns {Promise} Response con lista de usuarios móviles
 */
export const searchUsuariosMoviles = async (user, searchValue) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/conductores/search/usuarios-movil?searchValue=${encodeURIComponent(searchValue)}`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || [],
      total: response.data.total || 0,
      message: response.data.message || 'Usuarios móviles encontrados exitosamente'
    };
  } catch (error) {
    console.error('Error al buscar usuarios móviles:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al buscar usuarios móviles',
      data: [],
      total: 0
    };
  }
};

/**
 * Crear nuevo conductor y asignar a usuario móvil
 * @param {Object} user - Usuario autenticado
 * @param {number} usuarioMovilId - ID del usuario móvil
 * @param {Object} conductorData - Datos del conductor y asignación
 * @returns {Promise} Response del resultado
 */
export const createConductorUsuarioMovil = async (user, usuarioMovilId, conductorData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi}/mantenedores/conductores/usuarios-movil/${usuarioMovilId}/create-conductor`;
    const response = await axios.post(endpoint, conductorData, config);
    
    return {
      success: true,
      data: response.data,
      message: 'Conductor creado y asignado exitosamente'
    };
  } catch (error) {
    console.error('Error al crear conductor y asignar usuario móvil:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear conductor y asignar usuario móvil',
      details: error.response?.data
    };
  }
};

/**
 * Crear nuevo conductor
 * @param {Object} user - Usuario autenticado
 * @param {Object} conductorData - Datos del conductor y usuario móvil
 * @returns {Promise} Response del resultado
 */
export const createConductor = async (user, conductorData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi}/mantenedores/conductores/`;
    const response = await axios.post(endpoint, conductorData, config);
    
    return {
      success: true,
      data: response.data,
      message: response.data?.message || 'Conductor creado exitosamente'
    };
  } catch (error) {
    console.error('Error al crear conductor:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear conductor',
      details: error.response?.data
    };
  }
};

/**
 * Buscar conductores básicos por nombre, apellido o RUT
 * @param {Object} user - Usuario autenticado
 * @param {string} searchValue - Valor de búsqueda (nombre, apellido o RUT)
 * @returns {Promise} Response con lista de conductores básicos
 */
export const searchConductoresBasic = async (user, searchValue) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/conductores/search/basic?searchValue=${encodeURIComponent(searchValue)}`;
    const response = await axios.get(endpoint, config);

    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0,
        pageNumber: response.data.pageNumber || 1,
        pageSize: response.data.pageSize || 20,
        totalPages: response.data.totalPages || 1,
        message: response.data.message || 'Conductores encontrados exitosamente'
      };
    }

    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al buscar conductores básicos:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al buscar conductores básicos',
      data: [],
      total: 0
    };
  }
};
